//@ts-nocheck
import { useEffect, useRef, useCallback } from 'react';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Feature } from 'ol';
import { Map as OLMap } from 'ol';
import { Style, Stroke, Fill, Circle } from 'ol/style';
import { useMap } from '@/providers/MapProvider';

export const getSelectionStyle = (feature: Feature) => {
  const geometry = feature.getGeometry();
  if (!geometry) return [];

  const type = geometry.getType();
  const styles = [];

  if (type === 'Point') {
    styles.push(
      new Style({
        image: new Circle({
          radius: 12,
          fill: new Fill({ color: 'rgba(14, 165, 233, 0.3)' }), // Sky Blue 30%
        }),
      }),
    );
  } else {
    styles.push(
      new Style({
        stroke: new Stroke({
          color: 'rgba(14, 165, 233, 0.4)',
          width: 10,
        }),
        zIndex: 999,
      }),
    );
  }

  if (type === 'Point') {
    styles.push(
      new Style({
        image: new Circle({
          radius: 6,
          stroke: new Stroke({ color: '#0ea5e9', width: 2 }),
          fill: new Fill({ color: 'rgba(255,255,255,0.2)' }),
        }),
        zIndex: 1000,
      }),
    );
  } else {
    styles.push(
      new Style({
        stroke: new Stroke({ color: '#0ea5e9', width: 3 }),
        fill: new Fill({ color: 'rgba(14, 165, 233, 0.1)' }),
        zIndex: 1000,
      }),
    );
  }

  return styles;
};

export const useHighlightLayer = () => {
  const sourceRef = useRef<VectorSource | null>(null);
  const layerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const { map } = useMap();

  // Initialize Layer
  useEffect(() => {
    if (!map) return;

    const source = new VectorSource({ wrapX: false });
    const layer = new VectorLayer({
      source: source,
      style: getSelectionStyle,
      zIndex: 10000,
      properties: { type: 'highlight' },
    });

    map.addLayer(layer);
    sourceRef.current = source;
    layerRef.current = layer;

    return () => {
      if (map && layerRef.current) {
        map.removeLayer(layerRef.current);
      }
    };
  }, [map]);

  const cloneAndAdd = (original: Feature, source: VectorSource) => {
    const clone = original.clone();
    const id = original.getId();
    if (id) clone.setId(id);
    clone.setStyle(null); // Reset style so it uses Layer style
    source.addFeature(clone);
  };

  // Single click highlight
  const highlightFeature = useCallback((features: Feature | Feature[]) => {
    const source = sourceRef.current;
    if (!source) return;

    source.clear();

    const items = Array.isArray(features) ? features : [features];
    items.forEach((f) => {
      if (f) cloneAndAdd(f, source);
    });
  }, []);

  // Add highlight
  const addHighlight = useCallback((feature: Feature) => {
    const source = sourceRef.current;
    if (!source || !feature) return;

    // Prevent duplicates
    const id = feature.getId();
    if (id && source.getFeatureById(id)) return;

    cloneAndAdd(feature, source);
  }, []);

  const toggleHighlight = useCallback((feature: Feature) => {
    const source = sourceRef.current;
    if (!source || !feature) return;

    const id = feature.getId();
    if (!id) return; // Need ID to toggle

    const existing = source.getFeatureById(id);
    if (existing) {
      source.removeFeature(existing);
    } else {
      cloneAndAdd(feature, source);
    }
  }, []);

  const clearHighlight = useCallback(() => {
    sourceRef.current?.clear();
  }, []);

  return { highlightFeature, addHighlight, toggleHighlight, clearHighlight };
};
